// THE SHOP'S OWN PAGE FOR TEXTS, ON ITS WAIVER FORM'S HOST: https://<shop>.waivers.tattoo/texts (TW-0320 slice 2, 2026-10-17).
//
// Chris, 2026-10-16, reading the texting landing and its welcome: "also i think one thing clear is that waivers.tattoo is
// optional, and if they would prefer to setup texting with another system we're happy to do that" · "maybe some people want
// to text and might take a while to use something else? dunno". The texting welcome promises it: "Your waivers.tattoo waiver
// form is optional. If you'd rather keep the system your clients sign with now, just let me know and I'll set up texting
// with it." So a shop that keeps another waiver system builds its text list here: a client types a first and last name and a
// mobile number, ticks ONE box, and the promotions service writes the client row (services/promotions Promotions.TextSignup,
// whose moduledoc is the rule; this file is the two calls the page makes, the number box's own check and the page's words).
//
// WHERE IT LIVES, AND WHY HERE: the host every shop already has (its QR contract's, served by the wildcard CDN with no new
// zone), the one its texting registration names, and the one whose noscript copy a carrier's reviewer already reads
// (public/index.html). App.tsx draws it for the path `/texts` (isTextsPage) in place of the waiver form, none of the form's
// chrome around it: no paused screen (a shop's free waivers have nothing to do with its texts), no trial banner, no choice.
//
// THE RULES IT KEEPS:
//   · THE BOX'S WORDS ARE THE DOOR'S, never this bundle's: the page shows exactly what `GET /v1/text-signup` answers and posts
//     them back, and the door refuses words that are not the ones it would show now (`words_changed`), so what the row keeps
//     is always what was shown. English only (the Spanish waits on a native read, and the door keeps `language: "en"`).
//   · THE BROWSER CHECKS FIRST: `required` on every box, `type="tel"` with a `pattern` that is the door's own rule for a US
//     mobile (US_MOBILE_PATTERN, held to Promotions.TextSignup.us_mobile? by texts.test.ts), and the waiver form's lengths
//     (form-field-limits.json: 120, 120, 40). The door's own sentence is the backstop, shown as it comes.
//   · THE ANSWER IS THE SAME WHATEVER THE ROW HELD (the door's rule, so nobody can ask a shop's page who is on its list), so
//     the line after a yes is TRUE IN EVERY OUTCOME: a new client, one already on the list, one whose STOP or whose shop's
//     withdrawal stands (never undone here), a 2023 row the yes does not cover. It says what the client did and how the texts
//     come, never that texts will come (DONE_LINES below).
//   · A CLIENT'S NAME AND NUMBER GO TO THE DOOR AND NOWHERE ELSE: never into a beacon row (beacon.ts takes a closed list of
//     keys), a log or the address bar.
import { PROMOTIONS_URL } from "./config";
import { FIELD_LIMITS } from "./fieldLimits";

/** The page's path on every shop's host. */
export const TEXTS_PATH = "/texts";

/** Is this load the texts page? Read once at render, like App.tsx's `onTheForm`: this app does no client-side navigation. */
export function isTextsPage(pathname: string = typeof window === "undefined" ? "" : window.location.pathname): boolean {
  return pathname === TEXTS_PATH || pathname === `${TEXTS_PATH}/`;
}

const DOOR = "/v1/text-signup";

/** What the page draws: the box's words and the shop's name as its texts carry it, or closed, or the door did not answer. */
export type TextsPageState =
  | { state: "open"; name: string; words: string }
  | { state: "closed" }
  | { state: "unreachable"; message: string };

/** The press: a yes, or the door's code and its one sentence (`unreachable` when it did not answer). */
export type SignUpAnswer = { ok: true } | { ok: false; code: string; message: string; status: number };

export type Person = { first: string; last: string; phone: string };

/** The page's own sentences for the two answers the door cannot give itself: no answer at all. */
export const DID_NOT_LOAD = "This page didn't load. Check your connection, then try again.";
export const DID_NOT_GO = "That didn't go through. Check your connection, then try again.";

/** What the page draws for this shop: `GET /v1/text-signup?shop=`, a simple GET (no body, no content type, no preflight). */
export async function readTextsPage(subdomain: string, fetchFn: typeof fetch = fetch): Promise<TextsPageState> {
  try {
    const res = await fetchFn(`${PROMOTIONS_URL}${DOOR}?shop=${encodeURIComponent(subdomain)}`, { method: "GET" });
    const body = await res.json().catch(() => null);
    const data = body?.data;
    if (res.status === 200 && data?.open === true && typeof data?.words === "string" && data.words) {
      const name = typeof data?.shop?.name === "string" ? data.shop.name : "";
      return { state: "open", name, words: data.words };
    }
    if (res.status === 200 && data?.open === false) return { state: "closed" };
    const said = body?.error?.message;
    return { state: "unreachable", message: typeof said === "string" && said ? said : DID_NOT_LOAD };
  } catch {
    return { state: "unreachable", message: DID_NOT_LOAD };
  }
}

/** The press: the person as typed, the words exactly as shown, and which printed code brought them (`via`, or nothing). */
export async function signUp(
  subdomain: string,
  person: Person,
  words: string,
  via: string | undefined,
  fetchFn: typeof fetch = fetch
): Promise<SignUpAnswer> {
  try {
    const res = await fetchFn(`${PROMOTIONS_URL}${DOOR}`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        shop: subdomain,
        first: person.first,
        last: person.last,
        phone: person.phone,
        agreed: true,
        words,
        ...(via ? { via } : {}),
      }),
    });
    const body = await res.json().catch(() => null);
    if (res.status === 200 && body?.data?.signed_up === true) return { ok: true };
    const code = body?.error?.code;
    const message = body?.error?.message;
    return {
      ok: false,
      code: typeof code === "string" && code ? code : "unreachable",
      message: typeof message === "string" && message ? message : DID_NOT_GO,
      status: res.status,
    };
  } catch {
    return { ok: false, code: "unreachable", message: DID_NOT_GO, status: 0 };
  }
}

// ── the number box's own check ────────────────────────────────────────────────────────────────────

// A separator the door lets stand between digits: whitespace, parentheses, a dot, a plus and a hyphen, and nothing else
// (Promotions.TextSignup.us_mobile?: `\A[0-9()+.\-\s]+\z`). Escaped so the pattern compiles under the `v` flag, which
// browsers now use for the pattern attribute, and under `u`, which older ones use.
const SEP = "[\\s\\(\\)\\.\\+\\-]*";

/** THE DOOR'S OWN RULE FOR A US MOBILE, as the box's `pattern` (the browser anchors it): ten digits, or eleven with the 1 in
 *  front, the area code and the exchange each starting 2 to 9, between them only what SEP allows. Exactly
 *  Promotions.TextSignup.us_mobile?, so the browser stops what the door would refuse and nothing it would take. */
export const US_MOBILE_PATTERN =
  `${SEP}(?:1${SEP})?` + ["[2-9]", "[0-9]", "[0-9]", "[2-9]", "[0-9]", "[0-9]", "[0-9]", "[0-9]", "[0-9]", "[0-9]"].join(SEP) + SEP;

/** The pattern as the browser runs it: anchored, under `u` (texts.test.ts runs it under `v` too). */
export function looksLikeAUsMobile(value: string): boolean {
  return new RegExp(`^(?:${US_MOBILE_PATTERN})$`, "u").test(value);
}

/** The longest each box takes: the waiver form's own (form-field-limits.json), the door's too. */
export const LIMITS = { first: FIELD_LIMITS.firstName, last: FIELD_LIMITS.lastName, phone: FIELD_LIMITS.phoneNumber } as const;

// ── the page's words ──────────────────────────────────────────────────────────────────────────────

/** EVERY WORD THE PAGE SAYS BUT THE BOX'S (which are the door's). Plain, direct, warm; numerals; no exclamation mark; no
 *  promise a text will come. English only, the Spanish waiting on a native read. */
export const TEXTS_WORDS = {
  title: (name: string): string => (name ? `Get texts from ${name}` : "Get texts"),
  first: "First name",
  last: "Last name",
  phone: "Mobile number",
  phoneHint: "A US mobile number, like (317) 555-0123",
  phonePlaceholder: "(000) 000-0000",
  button: "Get texts",
  sending: "Sending",
  closed: (name: string): string => (name ? `Texts from ${name} aren't open here.` : "Texts from this shop aren't open here."),
  again: "Try again",
  privacy: "Privacy Policy",
  terms: "Terms",
  tab: (name: string): string => `${name ? `Get texts from ${name}` : "Get texts"} · waivers.tattoo`,
} as const;

/** THE LINES AFTER A YES, TRUE IN EVERY OUTCOME (the header says why the door's answer is one answer). They say what the
 *  client did and how the shop's texts come; they never say a text will come, which is not true for a client whose STOP
 *  stands, or a 2023 row this one box does not cover. */
export function doneLines(first: string, name: string): { title: string; lines: string[] } {
  const shop = name || "the shop";
  return {
    title: first ? `Thanks, ${first}.` : "Thanks.",
    lines: [
      `You said yes to texts from ${shop}: its news, offers and events, from its own number.`,
      `Each text ends with Reply STOP to opt out. If you replied STOP to ${shop} before, text START to that number to get its texts again.`,
    ],
  };
}
